import { ExamCenterProctorRequestType } from '@exam-center/graphql';
import { PaperSection, Text, Flex, Divider } from '@shared/components';
import { formatDate } from '@shared/utils';
import { useMemo } from 'react';

import {
  ProctorDocumentFields,
  ProctorEmployeeInformationFields,
  ProctorPersonalInformationFields,
  ProctorTypeField,
} from '../../components';
import { useAdminGetProctorRequestDownloadSignedUrlWithInfo } from '../../hooks';
import { useAdminExamCenterProctorRequestDetails } from '../../providers';

import { mapStorageObjectToFileNames } from './mapper';

function getProctorRequestEndDateTexts(hasEndedAt: boolean) {
  if (hasEndedAt) {
    return {
      firstText: 'กำหนดวันที่สิ้นสุดปฏิบัติหน้าที่',
      secondText: 'ระบุวันที่สิ้นสุดปฏิบัติหน้าที่ด้วยตนเอง',
    };
  }

  return {
    firstText: 'ไม่มีกำหนด',
    secondText: 'ยังไม่มีกำหนดวันที่สิ้นสุดปฏิบัติหน้าที่',
  };
}

export default function ExamCenterProctorRequestDetailsReadonlyField() {
  const { examCenterProctorRequest } = useAdminExamCenterProctorRequestDetails();
  const hasEndedAt = !!examCenterProctorRequest?.endedAt;
  const { firstText, secondText } = getProctorRequestEndDateTexts(hasEndedAt);

  const agreementStorageObjectsFile = useAdminGetProctorRequestDownloadSignedUrlWithInfo({
    examCenterProctorRequestId: examCenterProctorRequest.id,
    proctorRequestStorageObject: examCenterProctorRequest.agreementStorageObjects ?? [],
    options: { skip: !examCenterProctorRequest.id },
  });

  const personalInfoStorageObjectsFile = useAdminGetProctorRequestDownloadSignedUrlWithInfo({
    examCenterProctorRequestId: examCenterProctorRequest.id,
    proctorRequestStorageObject: examCenterProctorRequest?.personalInfoStorageObjects ?? [],
    options: { skip: !examCenterProctorRequest.id },
  });

  const agreementFiles = useMemo(
    () => mapStorageObjectToFileNames(agreementStorageObjectsFile),
    [agreementStorageObjectsFile],
  );
  const personalInfoFiles = useMemo(
    () => mapStorageObjectToFileNames(personalInfoStorageObjectsFile),
    [personalInfoStorageObjectsFile],
  );

  return (
    <PaperSection title='ข้อมูลผู้คุมสอบ' sx={{ borderRadius: (theme) => theme.shape.rounded(2) }}>
      <Flex
        sx={(theme) => ({
          flex: 1,
          gap: theme.spacing(6),
          flexDirection: 'column',
          p: theme.spacing(6),
        })}
      >
        <ProctorTypeField
          fields={{
            proctorType: {
              mode: 'view',
              value: (
                <Text>
                  {examCenterProctorRequest?.type === ExamCenterProctorRequestType.Proctor_1
                    ? 'ผู้คุมสอบประเภทที่ 1'
                    : 'ผู้คุมสอบประเภทที่ 2'}
                </Text>
              ),
            },
          }}
        />
        <Divider />
        <ProctorPersonalInformationFields
          fields={{
            idNumber: {
              mode: 'view',
              value: examCenterProctorRequest?.idNumber ? examCenterProctorRequest?.idNumber : '-',
            },
            titleTh: {
              mode: 'view',
              value: examCenterProctorRequest?.titleTh ? examCenterProctorRequest?.titleTh : '-',
            },
            firstNameTh: {
              mode: 'view',
              value: examCenterProctorRequest?.firstNameTh
                ? examCenterProctorRequest?.firstNameTh
                : '-',
            },
            lastNameTh: {
              mode: 'view',
              value: examCenterProctorRequest?.lastNameTh
                ? examCenterProctorRequest?.lastNameTh
                : '-',
            },
            titleEn: {
              mode: 'view',
              value: examCenterProctorRequest?.titleEn ? examCenterProctorRequest?.titleEn : '-',
            },
            firstNameEn: {
              mode: 'view',
              value: examCenterProctorRequest?.firstNameEn
                ? examCenterProctorRequest?.firstNameEn
                : '-',
            },
            lastNameEn: {
              mode: 'view',
              value: examCenterProctorRequest?.lastNameEn
                ? examCenterProctorRequest?.lastNameEn
                : '-',
            },
            phoneNumber: {
              mode: 'view',
              value: examCenterProctorRequest?.phoneNumber
                ? examCenterProctorRequest?.phoneNumber
                : '-',
            },
            email: {
              mode: 'view',
              value: examCenterProctorRequest?.email ? examCenterProctorRequest?.email : '-',
            },
            address: {
              mode: 'view',
              value: examCenterProctorRequest?.address ? examCenterProctorRequest?.address : '-',
            },
          }}
        />
        <Divider />
        <ProctorEmployeeInformationFields
          fields={{
            startedAt: {
              mode: 'view',
              value: examCenterProctorRequest?.startedAt
                ? formatDate(examCenterProctorRequest?.startedAt)
                : '-',
              slotProps: {
                label: 'วันที่เริ่มปฏิบัติหน้าที่',
              },
            },
            code: {
              mode: 'view',
              value: examCenterProctorRequest?.metadata?.employeeId
                ? examCenterProctorRequest?.metadata?.employeeId
                : '-',
              slotProps: {
                label: 'รหัสพนักงาน',
              },
            },
            position: {
              mode: 'view',
              value: examCenterProctorRequest?.metadata?.position
                ? examCenterProctorRequest?.metadata?.position
                : '-',
              slotProps: {
                label: 'ตำแหน่ง',
              },
            },
            department: {
              mode: 'view',
              value: examCenterProctorRequest?.metadata?.department
                ? examCenterProctorRequest?.metadata?.department
                : '-',
              slotProps: {
                label: 'ฝ่ายงาน/ส่วนงาน',
              },
            },
            isTerminationAssign: {
              mode: 'view',
              value: (
                <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
                  <Text>{firstText}</Text>
                  <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant='caption'>
                    {secondText}
                  </Text>
                </Flex>
              ),
            },
            endedAt: {
              mode: 'view',
              value: examCenterProctorRequest?.endedAt
                ? formatDate(examCenterProctorRequest?.endedAt)
                : '-',
              slotProps: {
                label: 'วันที่สิ้นสุดปฏิบัติหน้าที่',
              },
            },
          }}
          options={{
            shouldShowEndedAtDateDisplay: hasEndedAt,
          }}
        />
        <Divider />
        <ProctorDocumentFields
          fields={{
            agreementForms: {
              mode: 'view',
              slotProps: {
                files: agreementFiles,
              },
            },
            identifications: {
              mode: 'view',
              slotProps: {
                files: personalInfoFiles,
              },
            },
          }}
        />
      </Flex>
    </PaperSection>
  );
}
